import { CommonModule } from '@angular/common';
import { Component, type OnInit } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAX_MULTI_VALUE_FILTER_VALUES } from '@cdw-ae/common-data';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import {
  BaseMatcherEditorComponent,
  type MatcherComponentRegistryService,
} from '@cdw-ae/filter-bar-core';
import { MultiContainsMatcher } from '@cdw-ae/filter-bar-matcher-configs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'cdw-multi-contains-matcher',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CdwButtonModule,
    CdwIconModule,
    CommonUiComponentsTextInputModule,
  ],
  template: `
    <section [formGroup]="state">
      <div
        *ngIf="capWarning"
        class="cap-warning"
        role="alert"
        data-testid="multi-contains-cap-warning"
      >
        {{ capWarning }}
      </div>
      <div formArrayName="values">
        <ng-container
          *ngFor="let control of valuesArray.controls; let i = index; let last = last"
        >
          <span class="contains-label">Contains</span>
          <div class="value-row">
            <lib-common-ui-components-text-input
              [formControlName]="i"
              size="100%"
              [attr.data-testid]="'multi-contains-input-' + i"
            ></lib-common-ui-components-text-input>
            <div
              class="remove-action"
              [class.hidden]="valuesArray.length <= 1"
              (click)="removeRow(i)"
              (keydown.enter)="removeRow(i)"
              tabindex="0"
              role="button"
              aria-label="Remove value"
            >
              <lib-cdw-icon icon="delete" size="20px" customColor="var(--color-tertiary-blue-cta)"></lib-cdw-icon>
              <span class="remove-text">remove</span>
            </div>
          </div>
          <div *ngIf="!last" class="or-divider">
            <span class="or-text">OR</span>
          </div>
        </ng-container>
      </div>
      <lib-cdw-button
        *ngIf="valuesArray.length < MAX_ROWS"
        mode="basic"
        level="primary"
        [disabled]="!lastRowHasValue"
        (buttonClick)="addRow()"
      >
        + Add another filter
      </lib-cdw-button>
    </section>
  `,
  styleUrls: ['./multi-contains.matcher.scss'],
})
export class MultiContainsMatcherComponent
  extends BaseMatcherEditorComponent
  implements OnInit
{
  readonly MAX_ROWS = 5;

  public override state = new FormGroup({
    values: new FormArray<FormControl<string>>([]),
  });

  /**
   * Persistent over-cap message shown in the editor while the current input
   * exceeds the cap (null when within it). Replaces the transient snackbar so
   * the warning stays visible as long as it's relevant (EDUS-2575).
   */
  protected capWarning: string | null = null;

  constructor() {
    super();
    this.matcherDisplayName = MultiContainsMatcher.displayName;
    this.matcherName = MultiContainsMatcher.name;
  }

  public get valuesArray(): FormArray<FormControl<string>> {
    return this.state.controls.values;
  }

  public get lastRowHasValue(): boolean {
    if (this.valuesArray.length === 0) { 
      return false;
    }
    const last = this.valuesArray.at(this.valuesArray.length - 1);
    return !!last.value?.trim();
  }

  public ngOnInit(): void {
    // Populate from existing matchValue (editing an existing filter)
    const matchValue = this.matcherOptions.matchValue;
    if (matchValue && typeof matchValue === 'string') {
      try {
        const parsed = JSON.parse(matchValue);
        if (Array.isArray(parsed)) {
          parsed.forEach((v: string) => this.addRow(v));
        } else {
          this.addRow(matchValue);
        }
      } catch {
        this.addRow(matchValue);
      }
    } else {
      // Start with one empty row
      this.addRow();
    }

    // Emit combined value whenever any input changes
    this.valuesArray.valueChanges
      .pipe(startWith(this.valuesArray.value))
      .subscribe((values: string[]) => {
        const nonEmpty = this.enforceCap(values.filter((v) => v?.trim()));
        this.valueToMatch =
          nonEmpty.length > 0 ? JSON.stringify(nonEmpty) : null;
      });

    this.hasErrors$ = this.state.statusChanges.pipe(
      map((): boolean => this.state.invalid),
      startWith(this.state.invalid)
    );
  }

  public addRow(value = ''): void {
    this.valuesArray.push(
      new FormControl<string>(value, {
        nonNullable: true,
        validators: [Validators.required],
      })
    );
  }

  public removeRow(index: number): void {
    this.valuesArray.removeAt(index);
  }

  /**
   * Caps the applied value list at {@link MAX_MULTI_VALUE_FILTER_VALUES} (the
   * shared front/back cap). The rows are left intact (non-destructive) — only
   * the first N are applied — and {@link capWarning} surfaces a persistent,
   * explicit message in the editor while the input is over the cap (EDUS-2575).
   */
  private enforceCap(values: string[]): string[] {
    if (values.length > MAX_MULTI_VALUE_FILTER_VALUES) {
      const excess = values.length - MAX_MULTI_VALUE_FILTER_VALUES;
      this.capWarning =
        `Pasted ${values.length.toLocaleString()} values — only the first ` +
        `${MAX_MULTI_VALUE_FILTER_VALUES.toLocaleString()} will be applied ` +
        `(${excess.toLocaleString()} not applied).`;
      return values.slice(0, MAX_MULTI_VALUE_FILTER_VALUES);
    }
    this.capWarning = null;
    return values;
  }

  public static registerMultiContains(
    registry: MatcherComponentRegistryService
  ): void {
    registry.registerComponent(
      'multiContains',
      MultiContainsMatcherComponent
    );
  }
}
